// ============================================================================
// Payouts review (CW-N Phase 3).
//
// Opens on work that needs action, across every period — no picker, no "open
// a period first" prompt. Anything left over from an earlier month sorts to
// the top, because that is the thing most likely to be forgotten.
//
// Below that, two collapsed groups: completed payouts and disqualified ones.
// Disqualified stays visible (Jack's ruling) but out of the way.
//
// Two-step lifecycle: pending_review -> am_validated -> verified ->
// sent_to_finance -> paid, with a reversible 'disqualified' branch. Every
// transition is enforced by the DB; payout-actions.ts mirrors those rules and
// is the only place that decides which buttons exist.
// ============================================================================

import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { ErrorState } from '../../shared/components';
import { fmtCurrency } from '../../shared/format';
import { errorMessage } from '../../shared/errors';
import {
  INCENTIVE_PAYOUT_STATUS_LABELS,
  type IncentivePayoutStatus,
  type UserRole,
} from '../../shared/types';
import {
  usePayouts,
  usePayoutsNeedingAction,
  usePayoutsResolved,
  useEmployeeNames,
  setPayoutStatus,
  type PayoutDetail,
} from './hooks/use-incentive-data';
import {
  actionsFor,
  approvedVerb,
  capsForRole,
  validatedVerb,
  DISQUALIFY_REASON,
  engineReason,
  hasEngineReason,
  reinstateReason,
  withNote,
  type IncentiveCaps,
  type PayoutAction,
} from './payout-actions';
import type { PeriodSelection } from './index';
import { facilityWithSub, formatPeriod, payoutTypeShort, providerLabel } from './format';

const STATUS_CLS: Record<IncentivePayoutStatus, string> = {
  pending_review: 'bg-st-warn-bg text-st-warn-fg',
  // Info tint reads as "in progress" and stays distinct from the stronger
  // info tint used for 'verified'.
  am_validated: 'bg-st-info-bg text-st-info-fg',
  verified: 'bg-st-info-bg-strong text-st-info-fg',
  sent_to_finance: 'bg-violet-100 text-violet-700',
  paid: 'bg-rag-green/15 text-rag-green',
  disqualified: 'bg-tcs-surface-strong text-tcs-text-secondary',
};

// ---------------------------------------------------------------------------

export function PayoutsView({
  selection,
  role,
  refreshKey,
  onMutated,
  onClearSelection,
}: {
  selection: PeriodSelection | null;
  role: UserRole | null;
  refreshKey: number;
  onMutated: () => void;
  onClearSelection: () => void;
}) {
  const caps = capsForRole(role);

  // Drill-in: a specific period was opened from the queue. The all-periods
  // view is still one click away.
  if (selection) {
    return (
      <PeriodDrillIn
        selection={selection}
        caps={caps}
        refreshKey={refreshKey}
        onMutated={onMutated}
        onClearSelection={onClearSelection}
      />
    );
  }

  return <AllPeriods caps={caps} refreshKey={refreshKey} onMutated={onMutated} />;
}

// ----- Default view: everything that needs action --------------------------

function AllPeriods({
  caps,
  refreshKey,
  onMutated,
}: {
  caps: IncentiveCaps;
  refreshKey: number;
  onMutated: () => void;
}) {
  const outstanding = usePayoutsNeedingAction(refreshKey);
  const resolved = usePayoutsResolved(refreshKey);
  const nameFor = useEmployeeNames(
    [...outstanding.rows, ...resolved.rows].flatMap((r) => [
      r.verified_by_eid,
      r.am_validated_by_eid,
    ]),
  );

  if (outstanding.error)
    return <ErrorState body={outstanding.error} onRetry={outstanding.refetch} />;

  const completed = resolved.rows.filter((r) => r.status !== 'disqualified');
  const disqualified = resolved.rows.filter((r) => r.status === 'disqualified');

  return (
    <div className="space-y-5">
      <section>
        <h2 className="text-sm font-semibold text-tcs-text">Needs action</h2>
        <p className="mt-0.5 text-xs text-tcs-text-muted">
          Everything awaiting validation or approval, across all periods.
          Anything left over from an earlier period is at the top.
        </p>

        {outstanding.isLoading ? (
          <p className="mt-3 text-sm text-tcs-text-muted">Loading payouts…</p>
        ) : outstanding.rows.length === 0 ? (
          <p className="mt-3 rounded-lg border border-dashed border-tcs-border bg-tcs-surface p-4 text-sm text-tcs-text-muted">
            Nothing is waiting on you. Closed periods are in the groups below.
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {groupByPeriod(outstanding.rows).map((g) => (
              <PeriodGroup
                key={g.key}
                group={g}
                caps={caps}
                onMutated={onMutated}
                nameFor={nameFor}
                defaultOpen
              />
            ))}
          </div>
        )}
      </section>

      <CollapsedSection
        title="Completed payouts"
        subtitle="Approved, sent to finance and paid."
        rows={completed}
        isLoading={resolved.isLoading}
        error={resolved.error}
        caps={caps}
        onMutated={onMutated}
        nameFor={nameFor}
      />

      <CollapsedSection
        title="Disqualified"
        subtitle="Kept visible on purpose — a disqualification can be reinstated."
        rows={disqualified}
        isLoading={resolved.isLoading}
        error={resolved.error}
        caps={caps}
        onMutated={onMutated}
        nameFor={nameFor}
      />
    </div>
  );
}

interface Group {
  key: string;
  period: string;
  payoutType: PayoutDetail['payout_type'];
  rows: PayoutDetail[];
  total: number;
}

function groupByPeriod(rows: PayoutDetail[]): Group[] {
  const map = new Map<string, Group>();
  for (const r of rows) {
    const key = `${r.period}-${r.payout_type}`;
    let g = map.get(key);
    if (!g) {
      g = {
        key,
        period: r.period,
        payoutType: r.payout_type,
        rows: [],
        total: 0,
      };
      map.set(key, g);
    }
    g.rows.push(r);
    g.total += Number(r.amount || 0);
  }
  // Most recent period first; the query already returns them that way, but a
  // Map keeps insertion order and the query orders period desc, so this is a
  // belt-and-braces sort rather than a reshuffle.
  return [...map.values()].sort((a, b) => b.period.localeCompare(a.period));
}

function PeriodGroup({
  group,
  caps,
  onMutated,
  nameFor,
  defaultOpen,
}: {
  group: Group;
  caps: IncentiveCaps;
  onMutated: () => void;
  nameFor: (eid: string | null) => string | null;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline gap-2 rounded-md px-1 py-1 text-left hover:bg-tcs-bg-muted"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-tcs-text-muted" />
        )}
        <span className="text-sm font-semibold text-tcs-text">
          {formatPeriod(group.period)}
        </span>
        <span className="text-xs text-tcs-text-secondary">
          {payoutTypeShort(group.payoutType)}
        </span>
        <span className="ml-auto text-xs text-tcs-text-muted">
          {group.rows.length} row{group.rows.length === 1 ? '' : 's'} ·{' '}
          <span className="font-semibold text-tcs-text">
            {fmtCurrency(group.total)}
          </span>
        </span>
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {group.rows.map((r) => (
            <PayoutRow
              key={r.payout_id}
              row={r}
              caps={caps}
              onChanged={onMutated}
              nameFor={nameFor}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CollapsedSection({
  title,
  subtitle,
  rows,
  isLoading,
  error,
  caps,
  onMutated,
  nameFor,
}: {
  title: string;
  subtitle: string;
  rows: PayoutDetail[];
  isLoading: boolean;
  error: string | null;
  caps: IncentiveCaps;
  onMutated: () => void;
  nameFor: (eid: string | null) => string | null;
}) {
  const [open, setOpen] = useState(false);
  const total = rows.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <section className="rounded-xl border border-tcs-border bg-tcs-bg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        aria-expanded={open}
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-tcs-text-muted" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-tcs-text-muted" />
        )}
        <span className="text-sm font-semibold text-tcs-text">{title}</span>
        <span className="text-xs text-tcs-text-muted">{subtitle}</span>
        <span className="ml-auto shrink-0 text-xs text-tcs-text-muted">
          {isLoading ? '…' : `${rows.length} · ${fmtCurrency(total)}`}
        </span>
      </button>
      {open && (
        <div className="border-t border-tcs-border p-4">
          {error ? (
            <p className="text-xs text-st-danger-fg">{error}</p>
          ) : isLoading ? (
            <p className="text-sm text-tcs-text-muted">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-tcs-text-muted">Nothing here yet.</p>
          ) : (
            <div className="space-y-4">
              {groupByPeriod(rows).map((g) => (
                <PeriodGroup
                  key={g.key}
                  group={g}
                  caps={caps}
                  onMutated={onMutated}
                  nameFor={nameFor}
                  defaultOpen
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ----- Drill-in: one period, opened from the queue -------------------------

function PeriodDrillIn({
  selection,
  caps,
  refreshKey,
  onMutated,
  onClearSelection,
}: {
  selection: PeriodSelection;
  caps: IncentiveCaps;
  refreshKey: number;
  onMutated: () => void;
  onClearSelection: () => void;
}) {
  const { rows, isLoading, error, refetch } = usePayouts(
    selection.period,
    selection.payoutType,
    refreshKey,
  );
  const nameFor = useEmployeeNames(
    rows.flatMap((r) => [r.verified_by_eid, r.am_validated_by_eid]),
  );

  const totalPaying = rows
    .filter(
      (r) =>
        r.status === 'verified' ||
        r.status === 'sent_to_finance' ||
        r.status === 'paid',
    )
    .reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold text-tcs-text">
          {formatPeriod(selection.period)} ·{' '}
          <span className="font-normal text-tcs-text-secondary">
            {payoutTypeShort(selection.payoutType)}
          </span>
        </h2>
        <div className="flex items-center gap-3 text-xs text-tcs-text-muted">
          <span>
            Approved-to-pay:{' '}
            <span className="font-semibold text-tcs-text">
              {fmtCurrency(totalPaying)}
            </span>
          </span>
          <button
            type="button"
            onClick={onClearSelection}
            className="rounded border border-tcs-border bg-tcs-raised px-2 py-1 text-tcs-text hover:bg-tcs-surface"
          >
            Show all periods
          </button>
        </div>
      </div>

      {error && <ErrorState body={error} onRetry={refetch} />}
      {!error && isLoading && (
        <p className="text-sm text-tcs-text-muted">Loading payouts…</p>
      )}
      {!error && !isLoading && rows.length === 0 && (
        <p className="rounded-lg border border-dashed border-tcs-border bg-tcs-surface p-4 text-sm text-tcs-text-muted">
          No payouts generated for this period yet. Generate it from the
          Month-end queue.
        </p>
      )}

      <div className="space-y-2">
        {rows.map((r) => (
          <PayoutRow
            key={r.payout_id}
            row={r}
            caps={caps}
            onChanged={onMutated}
            nameFor={nameFor}
          />
        ))}
      </div>
    </div>
  );
}

// ----- One payout row -------------------------------------------------------

function stampLine(
  verb: string,
  who: string | null,
  when: string | null,
): string | null {
  if (!who && !when) return null;
  const day = when
    ? new Date(when).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
    : null;
  return `${verb} by ${who ?? 'unknown'}${day ? ` · ${day}` : ''}`;
}

function PayoutRow({
  row,
  caps,
  onChanged,
  nameFor,
}: {
  row: PayoutDetail;
  caps: IncentiveCaps;
  onChanged: () => void;
  nameFor: (eid: string | null) => string | null;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<PayoutAction | null>(null);

  const actions = actionsFor(row.status, row.payout_type, caps);
  const periodLabel = formatPeriod(row.period);

  async function apply(target: IncentivePayoutStatus, justification?: string) {
    setBusy(true);
    setError(null);
    try {
      await setPayoutStatus(row.payout_id, target, justification);
      setDialog(null);
      onChanged();
    } catch (e) {
      // A PostgrestError is a plain object — errorMessage() unwraps it, so a
      // policy rejection now reads as the sentence the DB actually raised.
      setError(errorMessage(e, 'Update failed.', 'incentive'));
    } finally {
      setBusy(false);
    }
  }

  function start(action: PayoutAction) {
    if (!action.needsReason) {
      void apply(action.target);
      return;
    }
    // The engine already wrote the reason onto the record — do not make the
    // AM restate it. Submit with what is on the row.
    if (action.kind === 'disqualify' && hasEngineReason(row)) {
      void apply(action.target, engineReason(row));
      return;
    }
    setDialog(action);
  }

  // Both stamps, once set — approving must never overwrite who validated.
  // The verb carries the tense: a send-back or an unapprove leaves the stamp
  // on the row (the DB keeps it on purpose), so it says "Previously validated"
  // rather than contradicting the status pill next to it.
  const validated = stampLine(
    validatedVerb(row.status),
    row.am_validated_by_name ?? nameFor(row.am_validated_by_eid),
    row.am_validated_at,
  );
  const approved = stampLine(
    approvedVerb(row.status),
    nameFor(row.verified_by_eid),
    row.verified_at,
  );

  return (
    <div className="rounded-lg border border-tcs-border bg-tcs-bg p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-tcs-text">
            {facilityWithSub(row.facility_name ?? row.facility_id, row)}
          </div>
          <div className="mt-0.5 text-xs text-tcs-text-muted">
            {providerLabel(row)} · {row.account_name ?? '—'}
            {row.market_region ? ` · ${row.market_region}` : ''} ·{' '}
            <span className="font-semibold text-tcs-text">
              {fmtCurrency(Number(row.amount || 0))}
            </span>
          </div>
        </div>
        <span
          className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_CLS[row.status]}`}
        >
          {INCENTIVE_PAYOUT_STATUS_LABELS[row.status]}
        </span>
      </div>

      {(validated || approved) && (
        <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-tcs-text-muted">
          {validated && <span>{validated}</span>}
          {approved && <span>{approved}</span>}
        </div>
      )}

      {row.disqualifying_feedback_desc && (
        <p className="mt-2 rounded bg-tcs-bg-muted px-2 py-1 text-xs text-tcs-text-secondary">
          Disqualified by feedback
          {row.disqualifying_feedback_date
            ? ` (${row.disqualifying_feedback_date})`
            : ''}
          : “{row.disqualifying_feedback_desc}”
        </p>
      )}
      {row.notes && (
        <p className="mt-1 whitespace-pre-line text-xs text-tcs-text-secondary">
          {row.notes}
        </p>
      )}
      {row.override_justification && (
        <p className="mt-1 text-xs text-violet-700">
          Reason: {row.override_justification}
        </p>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {actions.map((a) => (
          <ActBtn
            key={a.kind}
            variant={a.variant}
            onClick={() => start(a)}
            disabled={busy}
          >
            {a.label}
          </ActBtn>
        ))}
        {row.status === 'paid' && (
          <span className="text-xs text-tcs-text-muted">
            {row.payout_type === 'no_misses_monthly' ? 'Paid' : 'Given'}
            {row.paid_at ? ` · ${row.paid_at.slice(0, 10)}` : ''}
          </span>
        )}
      </div>

      {error && <p className="mt-2 text-xs text-st-danger-fg">{error}</p>}

      {dialog && (
        <ReasonDialog
          action={dialog}
          facilityName={row.facility_name ?? row.facility_id ?? 'this facility'}
          periodLabel={periodLabel}
          busy={busy}
          onCancel={() => setDialog(null)}
          onConfirm={(justification) => apply(dialog.target, justification)}
        />
      )}
    </div>
  );
}

function ActBtn({
  children,
  onClick,
  disabled,
  variant = 'primary',
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'primary' | 'ghost';
}) {
  const cls =
    variant === 'primary'
      ? 'bg-tcs-inverse text-tcs-inverse-ink hover:bg-tcs-inverse-hover'
      : 'border border-tcs-border bg-tcs-raised text-tcs-text hover:bg-tcs-surface';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-60 ${cls}`}
    >
      {children}
    </button>
  );
}

// ----- Reason dialog (CW-N 3c) ---------------------------------------------
//
// The reason is always the same, so it is written for the AM. An AM who keeps
// feedback and RAG current clears a row in one tap; the note is optional. The
// old dialog blocked submit until free text was typed — that is gone. What
// cannot go is a non-empty override_justification: the DB rejects an empty
// one, so the base sentence is always sent.

function ReasonDialog({
  action,
  facilityName,
  periodLabel,
  busy,
  onCancel,
  onConfirm,
}: {
  action: PayoutAction;
  facilityName: string;
  periodLabel: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (justification: string) => void;
}) {
  const [note, setNote] = useState('');
  const isDisqualify = action.kind === 'disqualify';
  const base = isDisqualify ? DISQUALIFY_REASON : reinstateReason(periodLabel);

  const title = isDisqualify
    ? 'Disqualify — poor cleaning outcome'
    : 'Reinstate — good cleaning outcome';

  const body = isDisqualify
    ? `Reason recorded: poor cleaning outcome. There's no feedback on record for ${facilityName} in ${periodLabel}. If you haven't recorded feedback that confirms this, would you like to add a brief note?`
    : `Reason recorded: good cleaning outcome for ${periodLabel}. Add a brief note?`;

  const hasNote = note.trim().length > 0;
  const confirmLabel = hasNote
    ? isDisqualify
      ? 'Disqualify with note'
      : 'Reinstate with note'
    : isDisqualify
      ? 'No — disqualify'
      : 'No — reinstate';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-lg bg-tcs-overlay p-5 shadow-xl"
      >
        <h3 className="text-sm font-semibold text-tcs-text">{title}</h3>
        <p className="mt-1 text-xs text-tcs-text-secondary">{body}</p>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder="Optional — anything the reason above doesn't cover."
          className="mt-3 w-full rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm focus:border-tcs-border-input focus:outline-none"
        />
        <p className="mt-1 text-[11px] text-tcs-text-muted">
          Recorded against this payout: “{withNote(base, note)}”
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md border border-tcs-border px-3 py-1.5 text-sm text-tcs-text-secondary hover:bg-tcs-bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            autoFocus
            disabled={busy}
            onClick={() => onConfirm(withNote(base, note))}
            className="rounded-md bg-tcs-inverse px-4 py-1.5 text-sm font-medium text-tcs-inverse-ink hover:bg-tcs-inverse-hover disabled:opacity-60"
          >
            {busy ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
